import type { FormikHandlers } from "formik";
import { type ChangeEvent, type FC, useId } from "react";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import "./MultiInput.css";
import { useEventCallback } from "usehooks-ts";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { ButtonIcon } from "./ButtonIcon";

type Props = {
	value: Array<string>;
	onChange: (value: Array<string>) => void;
	placeholder?: string;
	// formik props
	touched: boolean | undefined;
	error?: Array<string | null>;
	name: string;
	onBlur: FormikHandlers["handleBlur"];
};

type FormControlElement = HTMLInputElement | HTMLTextAreaElement;

export const MultiInput: FC<Readonly<Props>> = ({
	value,
	name,
	placeholder,
	error,
	onChange,
}) => {
	const id = useId();

	const handleAdd = useEventCallback(() => {
		const newValue = [...value, ""];
		onChange(newValue);
	});

	const handleChange = useEventCallback(
		(e: ChangeEvent<FormControlElement>, idx: number) => {
			const newValue = [...value];
			newValue[idx] = e.target.value;
			onChange(newValue);
		},
	);

	const handleDelete = useEventCallback((idx: number) => {
		const newValue = value.filter((_, i) => i !== idx);
		onChange(newValue);
	});

	return (
		<div className="multi-input">
			<div className="group">
				{value?.map((value, idx) => {
					const err = error?.[idx] ?? undefined;
					return (
						<div
							className="wrapper"
							// biome-ignore lint/suspicious/noArrayIndexKey: added key prefix
							key={`${id}-${idx}`}
						>
							<div className="group-row">
								<ExtraForm.Control
									type="text"
									name={`${name}-${id}-${idx}`}
									value={value}
									placeholder={placeholder}
									onChange={(e) => handleChange(e, idx)}
								/>
								{idx > 0 && (
									<div className="remove">
										<SimpleTooltip tooltip="Remove value" placement="bottom">
											<ButtonIcon
												icon="delete"
												onClick={() => handleDelete(idx)}
											/>
										</SimpleTooltip>
									</div>
								)}
							</div>

							{err && <p className="error">{err}</p>}
						</div>
					);
				})}
			</div>

			<div className="add">
				<SimpleTooltip tooltip="Add value" placement="bottom">
					<ButtonIcon icon="add_circle" onClick={handleAdd} />
				</SimpleTooltip>
			</div>
		</div>
	);
};
